import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  Play,
  CheckCircle2,
  Clock,
  Users,
  Star,
  BookOpen,
  Video,
  FileText,
  HelpCircle,
  Target,
  Award,
  Loader2,
  ChevronRight,
  GraduationCap,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const moduleTypeIcons = {
  video: Video,
  article: FileText,
  quiz: HelpCircle,
  practice: Target,
  reflection: BookOpen,
  resource: FileText,
};

const moduleTypeNames = {
  video: 'Video',
  article: 'Makale',
  quiz: 'Quiz',
  practice: 'Uygulama',
  reflection: 'Yansıma',
  resource: 'Kaynak',
};

const difficultyNames = {
  baslangic: 'Başlangıç',
  orta: 'Orta',
  ileri: 'İleri',
};

const difficultyColors = {
  baslangic: 'bg-green-100 text-green-700',
  orta: 'bg-yellow-100 text-yellow-700',
  ileri: 'bg-red-100 text-red-700',
};

const statusNames = {
  baslamadi: 'Başlanmadı',
  devam_ediyor: 'Devam Ediyor',
  tamamlandi: 'Tamamlandı',
};

const skillAreaNames = {
  akicilik: 'Akıcılık',
  cozumleme: 'Çözümleme',
  kelime: 'Kelime',
  anlama: 'Anlama',
  ses_bilgisi: 'Ses Bilgisi',
  yazma: 'Yazma',
  degerlendirme: 'Değerlendirme',
  mudahale: 'Müdahale',
  sinif_yonetimi: 'Sınıf Yönetimi',
  veli_katilimi: 'Veli Katılımı',
};

export const CoachingPage = () => {
  const { user, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('paths');
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [paths, setPaths] = useState([]);
  const [myProgress, setMyProgress] = useState(null);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    difficulty: 'all',
    skill_area: 'all',
  });
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const isAdmin = hasRole(['tenant_owner', 'center_director', 'reading_coach']);

  const fetchModules = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.difficulty !== 'all') params.append('difficulty', filters.difficulty);
      if (filters.skill_area !== 'all') params.append('skill_area', filters.skill_area);
      if (filters.search) params.append('search', filters.search);
      
      const response = await fetch(`${API_URL}/api/coaching/modules?${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setModules(data);
      }
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    }
  };

  const fetchPaths = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.difficulty !== 'all') params.append('difficulty', filters.difficulty);
      if (filters.skill_area !== 'all') params.append('skill_area', filters.skill_area);
      if (filters.search) params.append('search', filters.search);
      
      const response = await fetch(`${API_URL}/api/coaching/paths?${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPaths(data);
      }
    } catch (error) {
      console.error('Failed to fetch paths:', error);
    }
  };

  const fetchMyProgress = async () => {
    try {
      const response = await fetch(`${API_URL}/api/coaching/my-progress`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setMyProgress(data);
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/coaching/modules/stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchModules(), fetchPaths(), fetchMyProgress(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      if (activeTab === 'modules') {
        fetchModules();
      } else {
        fetchPaths();
      }
    }
  }, [filters.difficulty, filters.skill_area, activeTab]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (activeTab === 'modules') {
      fetchModules();
    } else {
      fetchPaths();
    }
  };

  const handleEnroll = async (pathId) => {
    setEnrolling(true);
    try {
      const response = await fetch(`${API_URL}/api/coaching/paths/${pathId}/enroll`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Öğrenme yoluna kaydoldunuz!');
        fetchPaths();
        fetchMyProgress();
        setSelectedPath(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kayıt başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setEnrolling(false);
    }
  };

  const handleStartModule = async (moduleId) => {
    try {
      const response = await fetch(`${API_URL}/api/coaching/modules/${moduleId}/start`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Modül başlatıldı!');
        fetchModules();
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleCompleteModule = async (moduleId) => {
    try {
      const response = await fetch(`${API_URL}/api/coaching/modules/${moduleId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ module_id: moduleId }),
      });
      
      if (response.ok) {
        toast.success('Modül tamamlandı!');
        fetchModules();
        fetchMyProgress();
        setSelectedModule(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Tamamlama başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const ModuleCard = ({ module }) => {
    const TypeIcon = moduleTypeIcons[module.module_type] || FileText;
    const progress = module.user_progress;
    const isCompleted = progress?.status === 'tamamlandi';
    const isInProgress = progress?.status === 'devam_ediyor';
    
    return (
      <Card 
        className={`hover:shadow-md transition-shadow cursor-pointer ${isCompleted ? 'border-green-200 bg-green-50/30' : ''}`}
        onClick={() => setSelectedModule(module)}
        data-testid={`module-card-${module.module_id}`}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isCompleted ? 'bg-green-100' : 'bg-primary/10'}`}>
                {isCompleted ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <TypeIcon className="h-5 w-5 text-primary" />
                )}
              </div>
              <Badge variant="outline" className={difficultyColors[module.difficulty]}>
                {difficultyNames[module.difficulty]}
              </Badge>
            </div>
            <Badge variant="secondary" className="text-xs">
              {moduleTypeNames[module.module_type]}
            </Badge>
          </div>
          
          <h3 className="font-semibold text-primary-900 line-clamp-2 mb-2">
            {module.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {module.description}
          </p>
          
          <div className="flex flex-wrap gap-1 mb-3">
            {module.skill_areas?.slice(0, 2).map((area, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {skillAreaNames[area] || area}
              </Badge>
            ))}
          </div>
          
          {isInProgress && (
            <div className="mb-3">
              <Progress value={progress.progress_percent} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {progress.progress_percent}% tamamlandı
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {module.duration_minutes} dk
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {module.completion_count}
              </span>
            </div>
            {module.average_rating > 0 && (
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                {module.average_rating.toFixed(1)}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const PathCard = ({ path }) => {
    const enrollment = path.user_enrollment;
    const isEnrolled = !!enrollment;
    const isCompleted = enrollment?.status === 'tamamlandi';
    
    return (
      <Card 
        className={`hover:shadow-md transition-shadow cursor-pointer ${isCompleted ? 'border-green-200 bg-green-50/30' : ''} ${path.is_featured ? 'ring-2 ring-secondary/50' : ''}`}
        onClick={() => setSelectedPath(path)}
        data-testid={`path-card-${path.path_id}`}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isCompleted ? 'bg-green-100' : 'bg-secondary/10'}`}>
                {isCompleted ? (
                  <Award className="h-5 w-5 text-green-600" />
                ) : (
                  <GraduationCap className="h-5 w-5 text-secondary" />
                )}
              </div>
              {path.is_featured && (
                <Badge className="bg-secondary text-white">
                  Öne Çıkan
                </Badge>
              )}
            </div>
            <Badge variant="outline" className={difficultyColors[path.difficulty]}>
              {difficultyNames[path.difficulty]}
            </Badge>
          </div>
          
          <h3 className="font-semibold text-primary-900 line-clamp-2 mb-2">
            {path.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {path.description}
          </p>
          
          <div className="flex flex-wrap gap-1 mb-3">
            {path.skill_areas?.slice(0, 3).map((area, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {skillAreaNames[area] || area}
              </Badge>
            ))}
          </div>
          
          {isEnrolled && (
            <div className="mb-3">
              <Progress value={enrollment.progress_percent} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {enrollment.progress_percent}% tamamlandı
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {path.estimated_hours} saat
              </span>
              <span className="flex items-center gap-1">
                <BookOpen className="h-3 w-3" />
                {path.module_count} modül
              </span>
            </div>
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {path.enrollment_count}
            </span>
          </div>
          
          {path.is_required && (
            <div className="mt-2 pt-2 border-t">
              <Badge variant="destructive" className="text-xs">
                Zorunlu Eğitim
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coaching-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900">
            Koçluk & Mikro Akademi
          </h1>
          <p className="text-muted-foreground">
            Mesleki gelişim eğitimleri ve öğrenme yolları
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary-900">{stats.published}</div>
                  <p className="text-xs text-muted-foreground">Modül</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{stats.user_completed}</div>
                  <p className="text-xs text-muted-foreground">Tamamladım</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{stats.user_in_progress}</div>
                  <p className="text-xs text-muted-foreground">Devam Ediyor</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <Award className="h-4 w-4 text-secondary" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-secondary">{myProgress?.summary?.completed_paths || 0}</div>
                  <p className="text-xs text-muted-foreground">Sertifika</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* My Progress */}
      {myProgress?.recent_enrollments?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Devam Eden Eğitimlerim</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {myProgress.recent_enrollments.filter(e => e.status === 'aktif').slice(0, 3).map((enrollment) => (
                <div 
                  key={enrollment.enrollment_id}
                  className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{enrollment.path_title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Progress value={enrollment.progress_percent} className="h-1.5 flex-1" />
                      <span className="text-xs text-muted-foreground">
                        {enrollment.progress_percent}%
                      </span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <Play className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="paths" data-testid="tab-paths">Öğrenme Yolları</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules">Tüm Modüller</TabsTrigger>
        </TabsList>

        {/* Filters */}
        <Card className="mt-4">
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={activeTab === 'paths' ? 'Öğrenme yolu ara...' : 'Modül ara...'}
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={filters.difficulty} onValueChange={(value) => setFilters({...filters, difficulty: value})}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Seviye" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Seviyeler</SelectItem>
                  {Object.entries(difficultyNames).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.skill_area} onValueChange={(value) => setFilters({...filters, skill_area: value})}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Beceri Alanı" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Alanlar</SelectItem>
                  {Object.entries(skillAreaNames).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" variant="secondary">
                Ara
              </Button>
            </form>
          </CardContent>
        </Card>

        <TabsContent value="paths" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paths.map(path => (
              <PathCard key={path.path_id} path={path} />
            ))}
          </div>
          
          {paths.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Öğrenme yolu bulunamadı
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="modules" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {modules.map(module => (
              <ModuleCard key={module.module_id} module={module} />
            ))}
          </div>
          
          {modules.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Modül bulunamadı
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Module Detail Dialog */}
      <Dialog open={!!selectedModule} onOpenChange={() => setSelectedModule(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedModule && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary">{moduleTypeNames[selectedModule.module_type]}</Badge>
                  <Badge variant="outline" className={difficultyColors[selectedModule.difficulty]}>
                    {difficultyNames[selectedModule.difficulty]}
                  </Badge>
                </div>
                <DialogTitle>{selectedModule.title}</DialogTitle>
                <DialogDescription>{selectedModule.description}</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {selectedModule.duration_minutes} dakika
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {selectedModule.completion_count} tamamlama
                  </span>
                  {selectedModule.average_rating > 0 && (
                    <span className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      {selectedModule.average_rating.toFixed(1)} ({selectedModule.rating_count})
                    </span>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {selectedModule.skill_areas?.map((area, idx) => (
                    <Badge key={idx} variant="outline">
                      {skillAreaNames[area] || area}
                    </Badge>
                  ))}
                </div>
                
                {selectedModule.content_text && (
                  <div className="prose prose-sm max-w-none bg-slate-50 p-4 rounded-lg">
                    <pre className="whitespace-pre-wrap font-sans text-sm">
                      {selectedModule.content_text}
                    </pre>
                  </div>
                )}
                
                {selectedModule.quiz_questions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Quiz Soruları ({selectedModule.quiz_questions.length})</h4>
                    <p className="text-sm text-muted-foreground">
                      Bu modülü tamamlamak için quiz sorularını yanıtlamanız gerekmektedir.
                    </p>
                  </div>
                )}
                
                {selectedModule.practice_tasks?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Uygulama Görevleri</h4>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {selectedModule.practice_tasks.map((task, idx) => (
                        <li key={idx}>{task}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedModule.reflection_prompts?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Yansıma Soruları</h4>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {selectedModule.reflection_prompts.map((prompt, idx) => (
                        <li key={idx}>{prompt}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedModule(null)}>
                  Kapat
                </Button>
                {selectedModule.user_progress?.status !== 'tamamlandi' && (
                  <>
                    {!selectedModule.user_progress ? (
                      <Button onClick={() => handleStartModule(selectedModule.module_id)}>
                        <Play className="mr-2 h-4 w-4" />
                        Başla
                      </Button>
                    ) : (
                      <Button onClick={() => handleCompleteModule(selectedModule.module_id)}>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Tamamla
                      </Button>
                    )}
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Path Detail Dialog */}
      <Dialog open={!!selectedPath} onOpenChange={() => setSelectedPath(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPath && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  {selectedPath.is_featured && (
                    <Badge className="bg-secondary text-white">Öne Çıkan</Badge>
                  )}
                  <Badge variant="outline" className={difficultyColors[selectedPath.difficulty]}>
                    {difficultyNames[selectedPath.difficulty]}
                  </Badge>
                  {selectedPath.is_required && (
                    <Badge variant="destructive">Zorunlu</Badge>
                  )}
                </div>
                <DialogTitle>{selectedPath.title}</DialogTitle>
                <DialogDescription>{selectedPath.description}</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {selectedPath.estimated_hours} saat
                  </span>
                  <span className="flex items-center gap-1">
                    <BookOpen className="h-4 w-4" />
                    {selectedPath.module_count} modül
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {selectedPath.enrollment_count} kayıt
                  </span>
                </div>
                
                {selectedPath.objectives?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Öğrenme Hedefleri</h4>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {selectedPath.objectives.map((obj, idx) => (
                        <li key={idx}>{obj}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2">
                  {selectedPath.skill_areas?.map((area, idx) => (
                    <Badge key={idx} variant="outline">
                      {skillAreaNames[area] || area}
                    </Badge>
                  ))}
                </div>
                
                {selectedPath.user_enrollment && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">İlerlemeniz</span>
                      <span className="text-sm text-muted-foreground">
                        {selectedPath.user_enrollment.progress_percent}%
                      </span>
                    </div>
                    <Progress value={selectedPath.user_enrollment.progress_percent} className="h-2" />
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedPath(null)}>
                  Kapat
                </Button>
                {!selectedPath.user_enrollment ? (
                  <Button onClick={() => handleEnroll(selectedPath.path_id)} disabled={enrolling}>
                    {enrolling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <GraduationCap className="mr-2 h-4 w-4" />
                    Kaydol
                  </Button>
                ) : selectedPath.user_enrollment.status !== 'tamamlandi' && (
                  <Button>
                    <Play className="mr-2 h-4 w-4" />
                    Devam Et
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoachingPage;
